import type { FC, ReactNode } from 'react'
import type { Profile } from '@/types'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { Menu, X } from 'lucide-react'

import {
    AnimatePresence,
    motion,
    useMotionValueEvent,
    useScroll
} from 'motion/react'

import { cn } from '@/lib/utils'
import { Container } from '@/components/generals/container'
import { useActiveSection } from '@/hooks/use-active-section'
import { EASE_OUT } from '@/constants/motion'

interface HeaderProps {
    profile: Profile | null
    isHome: boolean
}

const NAV_ITEMS = [
    { id: 'profile', label: 'Profile' },
    { id: 'about', label: 'About' },
    { id: 'work', label: 'Work' },
    { id: 'experience', label: 'Experience' },
    { id: 'skills', label: 'Skills' }
]

const MOBILE_ITEMS = [...NAV_ITEMS, { id: 'contact', label: 'Contact' }]

const SECTION_IDS = MOBILE_ITEMS.map(item => item.id)

// Teal top bar from the brand: hides while scrolling down, comes back on the
// way up, and underlines the section currently on screen.
export const Header: FC<HeaderProps> = ({ profile, isHome }): ReactNode => {
    const [hidden, setHidden] = useState(false)
    const [scrolled, setScrolled] = useState(false)
    const [open, setOpen] = useState(false)
    const { scrollY } = useScroll()
    const active = useActiveSection(isHome ? SECTION_IDS : [])
    const base = isHome ? '' : '/'

    useMotionValueEvent(scrollY, 'change', latest => {
        const previous = scrollY.getPrevious() ?? 0

        setScrolled(latest > 24)
        setHidden(latest > previous && latest > 200)
    })

    useEffect(() => {
        if (!open) return

        const { overflow } = document.body.style
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false)
        }

        document.body.style.overflow = 'hidden'
        window.addEventListener('keydown', handleKeyDown)

        return () => {
            document.body.style.overflow = overflow
            window.removeEventListener('keydown', handleKeyDown)
        }
    }, [open])

    const close = () => setOpen(false)

    return (
        <>
            <motion.header
                className='fixed inset-x-0 top-0 z-50'
                initial={false}
                animate={{ y: hidden && !open ? '-110%' : '0%' }}
                transition={{ duration: 0.4, ease: EASE_OUT }}
            >
                <div
                    className={cn(
                        'bg-secondary text-secondary-foreground transition-shadow duration-300',
                        scrolled && 'shadow-[0_12px_30px_-16px_rgba(0,0,0,0.5)]'
                    )}
                >
                    <Container className='flex h-16 items-center justify-between gap-4'>
                        <Link
                            href={isHome ? '#profile' : '/'}
                            onClick={close}
                            className='inline-flex items-center whitespace-nowrap rounded-full bg-primary px-4 py-2 font-mono text-xs font-bold uppercase tracking-[0.18em] text-primary-foreground transition-transform duration-300 hover:-rotate-2 hover:scale-105'
                        >
                            {profile ? (
                                <span>
                                    <span data-mdlfy-cms={`profile:${profile.id}:first_name`}>{profile.first_name}</span>
                                    {' '}
                                    <span data-mdlfy-cms={`profile:${profile.id}:last_name`}>{profile.last_name}</span>
                                </span>
                            ) : 'Portfolio'}
                        </Link>

                        <nav aria-label='Main' className='hidden items-center gap-1 md:flex'>
                            {NAV_ITEMS.map(item => {
                                const isActive = active === item.id

                                return (
                                    <Link
                                        key={item.id}
                                        href={`${base}#${item.id}`}
                                        aria-current={isActive ? 'true' : undefined}
                                        className='relative rounded-full px-3 py-2 font-mono text-xs uppercase tracking-[0.16em] transition-colors hover:bg-white/10'
                                    >
                                        {item.label}

                                        {isActive && (
                                            <motion.span
                                                layoutId='nav-indicator'
                                                aria-hidden='true'
                                                className='absolute inset-x-3 bottom-0.5 h-0.5 rounded-full bg-primary'
                                                transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                                            />
                                        )}
                                    </Link>
                                )
                            })}

                            <Link
                                href={`${base}#contact`}
                                className='ml-3 inline-flex items-center rounded-full bg-accent px-4 py-2 font-mono text-xs font-bold uppercase tracking-[0.16em] text-accent-foreground transition-transform duration-300 hover:-translate-y-0.5'
                            >
                                Contact
                            </Link>
                        </nav>

                        <button
                            type='button'
                            onClick={() => setOpen(value => !value)}
                            aria-expanded={open}
                            aria-controls='mobile-menu'
                            aria-label={open ? 'Close menu' : 'Open menu'}
                            className='inline-flex size-11 items-center justify-center rounded-full bg-white/15 transition-colors hover:bg-white/25 md:hidden'
                        >
                            {open ? <X className='size-5' /> : <Menu className='size-5' />}
                        </button>
                    </Container>
                </div>
            </motion.header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        id='mobile-menu'
                        className='fixed inset-x-0 bottom-0 top-16 z-40 overflow-y-auto bg-secondary text-white md:hidden'
                        initial={{ clipPath: 'inset(0% 0% 100% 0%)' }}
                        animate={{ clipPath: 'inset(0% 0% 0% 0%)' }}
                        exit={{ clipPath: 'inset(0% 0% 100% 0%)' }}
                        transition={{ duration: 0.55, ease: EASE_OUT }}
                    >
                        <nav aria-label='Mobile'>
                            <ul className='flex flex-col gap-1 px-5 pt-8 pb-12'>
                                {MOBILE_ITEMS.map((item, index) => (
                                    <motion.li
                                        key={item.id}
                                        initial={{ opacity: 0, y: 28 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.5, ease: EASE_OUT, delay: 0.12 + index * 0.05 }}
                                    >
                                        <Link
                                            href={`${base}#${item.id}`}
                                            onClick={close}
                                            className='flex items-baseline gap-4 py-2 font-heading text-5xl font-extrabold tracking-tight transition-colors hover:text-primary'
                                        >
                                            <span className='font-mono text-xs font-normal tracking-[0.2em]'>
                                                {String(index + 1).padStart(2, '0')}
                                            </span>

                                            {item.label}
                                        </Link>
                                    </motion.li>
                                ))}
                            </ul>
                        </nav>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    )
}
